import { Box, FormControl } from '@mui/material'

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker'
import dayjs, { Dayjs } from 'dayjs'
import { ISO_DATE_TIME_FORMAT_Z } from '../../utils/DateAndTimeUtils'
import { useCallback, useEffect, useMemo, useState, memo } from 'react'
import {
    DateRange,
    PickersActionBar,
    PickersActionBarProps,
    PickersShortcutsItem,
} from '@mui/x-date-pickers-pro'
import { useAppSelector } from '../../redux/hook'

interface DateRangeInputProps {
    id: string
    label: string
    placeholderLabel?: string
    value: any[]
    onChange: (id: string, value: (string | null)[]) => void
    errorMessage?: string
    required?: boolean
    disabled?: boolean
    clearable?: boolean
    maxDate?: Dayjs
    minDate?: Dayjs
}

const DateRangeInput = (props: DateRangeInputProps): JSX.Element => {
    const {
        id,
        label,
        placeholderLabel,
        value,
        onChange,
        errorMessage = '',
        required,
        disabled,
        clearable,
        maxDate,
        minDate,
    } = props

    const userLocalization = useAppSelector(
        (state) => state.user.user?.preferences?.localization,
    )

    const userTimeZone = useMemo(
        () => userLocalization?.timeZone || dayjs.tz.guess(),
        [userLocalization],
    )
    const userFormat = useMemo(
        () => userLocalization?.dateFormat || 'MM/DD/YYYY',
        [userLocalization],
    )
    const [val, setVal] = useState<DateRange<dayjs.Dayjs>>([null, null]) //final value, updated only when click on "ok"
    const [temVal, setTemVal] = useState<DateRange<dayjs.Dayjs>>([null, null]) //temporary value, update every time when user select date

    const [open, setOpen] = useState<boolean>(false)

    //used this to show placeholder as lable when there is no value
    const [focused, setFocused] = useState(false)

    const shortcutsItems = useMemo<PickersShortcutsItem<DateRange<Dayjs>>[]>(
        () => [
            {
                label: 'Today',
                getValue: () => {
                    const todayStartTime = dayjs()
                        .tz(userTimeZone)
                        .startOf('day')
                    const todayEndTime = dayjs().tz(userTimeZone).endOf('day')
                    return [todayStartTime, todayEndTime]
                },
            },
        ],
        [userTimeZone],
    )
    useEffect(() => {
        if (value && value.length > 0) {
            setVal([
                value[0] ? dayjs(value[0]).tz(userTimeZone) : null,
                value[1] ? dayjs(value[1]).tz(userTimeZone) : null,
            ])
            setTemVal([
                value[0] ? dayjs(value[0]).tz(userTimeZone) : null,
                value[1] ? dayjs(value[1]).tz(userTimeZone) : null,
            ])
        }
    }, [value, userTimeZone])

    const handleChangeDateRange = useCallback(
        (newValue: DateRange<dayjs.Dayjs>) => {
            const range: DateRange<dayjs.Dayjs> = [
                newValue[0]?.tz(userTimeZone, true).startOf('day') || null,
                newValue[1]?.tz(userTimeZone, true).endOf('day') || null,
            ]
            setTemVal(range)
        },
        [userTimeZone],
    )

    const handleSubmitDateRange = useCallback(
        (newValue?: DateRange<Dayjs>) => {
            const updatedVal = newValue?.[0] && newValue?.[1] ? newValue : val
            const updateNewValue = [
                updatedVal[0]
                    ? dayjs(updatedVal[0]).utc().format(ISO_DATE_TIME_FORMAT_Z)
                    : null,
                updatedVal[1]
                    ? dayjs(updatedVal[1]).utc().format(ISO_DATE_TIME_FORMAT_Z)
                    : null,
            ]
            setVal(updatedVal)
            setTemVal(updatedVal)
            onChange(id, updateNewValue)
        },
        [val, id, onChange],
    )

    const onClickOk = useCallback(() => {
        handleSubmitDateRange(temVal)
    }, [handleSubmitDateRange, temVal])

    const handleCloseDateRange = useCallback(() => {
        handleSubmitDateRange()
        setOpen(false)
    }, [handleSubmitDateRange])

    const onClearDateRange = useCallback(() => {
        onChange(id, [null, null])
        setTemVal([null, null])
        setVal([null, null])
    }, [onChange, id])
    return (
        <Box sx={{ width: '100%' }}>
            <FormControl
                fullWidth
                disabled={!!disabled}
                size="small"
                error={!!errorMessage}
                required={required}
            >
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DateRangePicker
                        value={temVal}
                        timezone={userTimeZone}
                        open={open}
                        onOpen={() => setOpen(true)}
                        onClose={handleCloseDateRange}
                        onChange={(newValue) => handleChangeDateRange(newValue)}
                        onAccept={(newValue) => handleChangeDateRange(newValue)}
                        closeOnSelect={false}
                        label={
                            val[0] || val[1] || focused || open
                                ? label
                                : placeholderLabel || ''
                        }
                        disableHighlightToday
                        reduceAnimations
                        calendars={1}
                        slotProps={{
                            textField: {
                                size: 'small',
                                InputProps: { readOnly: true }, // disable typing
                                onChange: () => {},
                                onClick: (e) => {
                                    // let MUI handle open internally
                                    const calendarButton =
                                        e.currentTarget.querySelector(
                                            "button[aria-label='Choose range']",
                                        ) as HTMLElement

                                    calendarButton?.click()
                                },
                                onFocus: () => setFocused(true),
                                onBlur: () => setFocused(false),
                                sx: {
                                    bgcolor: '#fff',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    '.clearButton': {
                                        padding: '0px',
                                    },
                                    '& label': {
                                        color: '#000000de',
                                    },
                                },
                            },
                            field: {
                                clearable,
                                onClear: () => {
                                    onClearDateRange()
                                },
                                onKeyDown: (e) => {
                                    e.preventDefault() //  blocks ALL typing
                                },
                                onPaste: (e) => {
                                    e.preventDefault() //blocks paste
                                },
                            },
                            desktopPaper: {
                                sx: {
                                    '& .MuiDayCalendar-slideTransition': {
                                        minHeight: '215px',
                                    },
                                },
                            },
                            actionBar: {
                                actions: ['cancel', 'accept'],
                                sx: {
                                    paddingBottom: '20px',
                                    '& .MuiButton-root:nth-of-type(2)':
                                        temVal[0] && temVal[1]
                                            ? {}
                                            : {
                                                  color: '#9e9e9e',
                                                  cursor: 'not-allowed',
                                                  pointerEvents: 'none',
                                              },
                                },
                            },
                            shortcuts: {
                                items: shortcutsItems,
                                changeImportance: 'set',
                                sx: {
                                    position: 'absolute',
                                    bottom: '7px',
                                    left: '0',
                                    '& div': {
                                        fontSize: '13px',
                                    },
                                },
                            },
                        }}
                        slots={{
                            actionBar: (props: PickersActionBarProps) => (
                                <PickersActionBar
                                    {...props}
                                    actions={['cancel', 'accept']}
                                    onClick={(e) => {
                                        const target = (e.target as HTMLElement)
                                            .textContent
                                        if (target === 'Cancel') {
                                            handleSubmitDateRange()
                                        } else {
                                            onClickOk()
                                        }
                                    }}
                                />
                            ),
                        }}
                        localeText={{ start: 'Check-in', end: 'Check-out' }}
                        maxDate={maxDate ?? undefined}
                        minDate={minDate ?? undefined}
                        format={userFormat}
                    />
                </LocalizationProvider>
            </FormControl>
        </Box>
    )
}
const MemoizedDateRangeInput = memo(DateRangeInput)
MemoizedDateRangeInput.displayName = 'DateRangeInput'
export default MemoizedDateRangeInput
